/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import type { JSX } from 'preact';
import { classNames } from '#utils/UNSAFE_classNames';
import { ForwardedRef, forwardRef } from 'preact/compat';
import type { UnpackSignals } from '@oracle/oraclejet-internal-utilities/attributeUtils';
import { styles } from './themes/LegendStyles.css';
import { getLegendCurrentItemStyle } from '#utils/PRIVATE_legendUtils/legendUtils';
import { SymbolProps, TextStylesProps } from '#utils/UNSAFE_visTypes/legend';
import { LegendSymbol } from './LegendSymbol';
import { LegendText } from './LegendText';

type BaseLegendItemProps = {
  text: string;
  itemIdx: number;
  sectionIdx: number;
  itemId?: number | string;
  isCurrent: boolean;
  isFocused: boolean;
  isHighlighted?: boolean;
  isHidden?: boolean;
  symbolWidth?: number;
  symbolHeight?: number;
  id?: string;
  'aria-label'?: UnpackSignals<JSX.AriaAttributes>['aria-label'];
  class?: string;
  minWidth?: number;
  gridCol?: number;
  gridRow?: number;
  hideAndShowBehavior?: 'on' | 'off';
  actionable?: 'off' | 'inherit';
  hoverBehavior?: 'dim' | 'none';
} & SymbolProps &
  TextStylesProps;

export const BaseLegendItem = forwardRef(
  (
    {
      id,
      text,
      sectionIdx,
      itemIdx,
      itemId,
      isHighlighted,
      symbolHeight,
      symbolWidth,
      isFocused,
      isCurrent,
      isHidden,
      actionable = 'inherit',
      hoverBehavior = 'none',
      hideAndShowBehavior = 'off',
      ...props
    }: BaseLegendItemProps,
    ref: ForwardedRef<HTMLDivElement>
  ) => {
    const textStyles = {
      fontFamily: props.textFontFamily,
      fontSize: props.textFontSize,
      color: props.textColor,
      fontStyle: props.textFontStyle,
      fontWeight: props.textFontWeight,
      textDecoration: props.textTextDecoration
    };

    const symbolProps = {
      lineStyle: props.lineStyle,
      lineWidth: props.lineWidth,
      markerShape: props.markerShape,
      markerColor: props.markerColor,
      source: props.source,
      borderColor: props.borderColor,
      lineColor: props.lineColor
    };

    const isNotDimmed = isHighlighted || isHighlighted == null;
    const { legendItemBaseStyle, legendItemOpacity, legendItemFocusRing } = styles;

    return (
      <div
        class={classNames([
          legendItemBaseStyle,
          getLegendCurrentItemStyle(
            isCurrent,
            isHighlighted,
            hideAndShowBehavior,
            hoverBehavior,
            isHidden,
            actionable
          ),
          !isNotDimmed ? legendItemOpacity : '',
          isFocused ? legendItemFocusRing : '',
          props.class
        ])}
        style={{
          gridRow: props.gridRow,
          gridColumn: props.gridCol,
          minWidth: props.minWidth !== undefined ? `${props.minWidth}px` : undefined
        }}
        id={id}
        ref={ref}
        data-oj-private-section={sectionIdx}
        data-oj-private-item={itemIdx}
        data-oj-private-item-id={itemId}
        role={isHidden != null ? 'menuitemcheckbox' : 'img'}
        aria-checked={isHidden != null ? !isHidden : undefined}
        aria-label={props['aria-label'] || text}>
        <LegendSymbol
          {...symbolProps}
          isHidden={isHidden}
          lineLength={symbolWidth}
          width={symbolWidth != null ? `${symbolWidth}px` : undefined}
          height={symbolHeight != null ? `${symbolHeight}px` : undefined}
        />
        <LegendText {...textStyles} text={text} type="label" />
      </div>
    );
  }
);
